import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Cpu, Workflow, Layers, Calculator, Download, BadgeDollarSign,
  BoxSelect, Zap, ShieldCheck, Search, MousePointerClick,
  Users, TrendingUp, Package, FileText, CreditCard, UserCheck,
  BarChart3, Kanban, Headphones, FileSignature, Shield, Megaphone,
  Landmark, LineChart, CheckSquare, RefreshCw, MessageSquare,
  ShieldAlert, ArrowUpRight, X, CheckCircle2, TrendingDown, Box, Home,
} from "lucide-react";
// Live operator surfaces embedded into the prospect demo — see
// <NdsLiveEmbeds /> at the bottom.  They read the same public
// /api/nex-os/* endpoints the /os console uses, and every mutation is
// short-circuited via <DemoModeProvider value>.
import { NexOsProvider } from "@/lib/nexOs";
import { FloatingWindowsProvider } from "@/lib/floatingWindows";
import { NdsEditorProvider } from "@/lib/ndsEditor";
import { DemoModeProvider } from "@/lib/demoMode";
import { LIFE_LOGO } from "@/lib/assets";
import NexWordmark from "@/components/NexWordmark";
import KpiPulseBar from "@/components/nds/KpiPulseBar";
import LiveSystemFeed from "@/components/nds/LiveSystemFeed";
import OpsKpiBar from "@/components/nds/OpsKpiBar";
import TenStepRevenueFlow from "@/components/nds/TenStepRevenueFlow";
import CapabilityRoadmap from "@/components/nds/CapabilityRoadmap";
import WindowLayer from "@/components/nds/windows/WindowLayer";
import AccordionSection from "@/components/nds/AccordionSection";

/* ── Full 20-module dataset — verbatim from the source HTML export ── */
const CATEGORIES = [
  { id:"crm",         title:"CRM & Sales Pipeline",        desc:"Tracks customer leads, contact history, and deal stages.",              tag:"CRM • ACTIVE",         tagColor:"border-blue-500/40 text-blue-400 bg-blue-500/10",         legacy:"Salesforce / HubSpot", category:"sales",        icon:"users",           costPerUser:125, integration:"Continuous bi-directional contact & opportunity sync via native REST & Webhooks.",         replacement:"Built-in pipeline automation, email sequence tracker, and AI lead scoring." },
  { id:"pnl",         title:"Profit & Loss (P&L)",          desc:"Generates real-time income, margin, and expense statements.",           tag:"FINANCE • LIVE",       tagColor:"border-emerald-500/40 text-emerald-400 bg-emerald-500/10", legacy:"QuickBooks / NetSuite", category:"finance",       icon:"trending-up",     costPerUser: 65, integration:"Real-time ledger posting and general ledger synchronization.",                             replacement:"Native double-entry ledger engine with real-time continuous profit calculations." },
  { id:"inventory",   title:"Inventory Management",         desc:"Monitors multi-warehouse stock levels and reorder triggers.",           tag:"STOCK • SYNCED",       tagColor:"border-cyan-500/40 text-cyan-400 bg-cyan-500/10",         legacy:"Zoho / Fishbowl",       category:"finance",       icon:"package",         costPerUser: 45, integration:"Low-latency SKU level inventory polling and order status webhooks.",                       replacement:"Multi-location inventory tracking with predictive automated reorder triggers." },
  { id:"tax",         title:"Tax & Compliance",             desc:"Calculates real-time jurisdictional tax and auto-files returns.",       tag:"TAX • AUTOMATED",      tagColor:"border-amber-500/40 text-amber-400 bg-amber-500/10",       legacy:"Avalara / TaxJar",      category:"finance",       icon:"file-text",       costPerUser: 35, integration:"API address validation and instant tax liability calculation on checkout.",                replacement:"Automated global tax lookup engine integrated directly into invoicing." },
  { id:"invoicing",   title:"Invoicing & Payments",         desc:"Automates recurring subscriptions and payment collections.",            tag:"BILLING • ACTIVE",     tagColor:"border-purple-500/40 text-purple-400 bg-purple-500/10",   legacy:"Stripe / Square",       category:"finance",       icon:"credit-card",     costPerUser: 40, integration:"Webhook-driven transaction status updates and merchant account bridging.",                 replacement:"Native credit card, ACH, and wire processing gateway with 0% markup." },
  { id:"payroll",     title:"Payroll & HR Ops",             desc:"Executes direct deposits, benefits admin, and time tracking.",          tag:"HR • RUNNING",         tagColor:"border-rose-500/40 text-rose-400 bg-rose-500/10",         legacy:"Gusto / Workday",       category:"hr",            icon:"user-check",      costPerUser: 55, integration:"Employee sync and direct pay stub export integrations.",                                   replacement:"Automated payroll distribution, tax filing, and time tracking dashboard." },
  { id:"automation",  title:"Workflow Automation",          desc:"Connects disparate software with automated triggers & webhooks.",       tag:"LOGIC • SYNCED",       tagColor:"border-orange-500/40 text-orange-400 bg-orange-500/10",   legacy:"Zapier / Make",         category:"productivity",  icon:"zap",             costPerUser: 30, integration:"Exposes all engine events to external webhook listeners.",                                 replacement:"Native no-code event trigger bus eliminating external task charges." },
  { id:"bi",          title:"BI & Data Analytics",          desc:"Visualizes company KPIs and custom executive dashboards.",              tag:"BI • REALTIME",        tagColor:"border-blue-500/40 text-blue-400 bg-blue-500/10",         legacy:"Tableau / Looker",      category:"productivity",  icon:"bar-chart-3",     costPerUser: 70, integration:"Direct SQL read-replica streaming into custom BI tools.",                                  replacement:"Built-in drag-and-drop analytics, custom KPI builder, and scheduled reporting." },
  { id:"project",     title:"Project Management",           desc:"Organizes sprint tasks, Gantt timelines, and team workloads.",          tag:"TASKS • ACTIVE",       tagColor:"border-emerald-500/40 text-emerald-400 bg-emerald-500/10", legacy:"Asana / Monday",        category:"productivity",  icon:"kanban",          costPerUser: 25, integration:"Two-way task status and milestone completion webhook synchronization.",                    replacement:"Unified Kanban, Gantt, and workload allocation board linked to live deals." },
  { id:"helpdesk",    title:"Customer Helpdesk",            desc:"Manages support tickets, live chat, and knowledge bases.",              tag:"HELPDESK • LIVE",      tagColor:"border-cyan-500/40 text-cyan-400 bg-cyan-500/10",         legacy:"Zendesk / Intercom",    category:"sales",         icon:"headphones",      costPerUser: 60, integration:"Inbound ticket sync and customer profile enrichment across platforms.",                    replacement:"Native multi-channel ticketing, live chat widget, and AI resolution bot." },
  { id:"vault",       title:"Document Vault & Sign",        desc:"Stores contracts securely and collects binding electronic signatures.", tag:"VAULT • SECURE",       tagColor:"border-amber-500/40 text-amber-400 bg-amber-500/10",       legacy:"DocuSign / PandaDoc",   category:"productivity",  icon:"file-signature",  costPerUser: 30, integration:"Automated envelope status triggers and PDF metadata ingestion.",                           replacement:"Native e-signature generation and ESIGN-compliant document vault." },
  { id:"sso",         title:"Identity & Access (SSO)",      desc:"Controls user single sign-on, MFA, and role permissions.",              tag:"SSO • ENFORCED",       tagColor:"border-purple-500/40 text-purple-400 bg-purple-500/10",   legacy:"Okta / Auth0",          category:"hr",            icon:"shield",          costPerUser: 20, integration:"SAML 2.0 / OIDC enterprise identity provider bridging.",                                   replacement:"Built-in RBAC, MFA, and directory management zero-trust framework." },
  { id:"marketing",   title:"Marketing Automation",         desc:"Triggers segmented email drip campaigns and lead nurturing.",           tag:"CAMPAIGNS • ACTIVE",   tagColor:"border-rose-500/40 text-rose-400 bg-rose-500/10",         legacy:"Mailchimp / Klaviyo",   category:"sales",         icon:"megaphone",       costPerUser: 40, integration:"Audience segment synchronization and campaign activity reporting.",                        replacement:"Integrated email builder, automated user journeys, and dynamic segmentation." },
  { id:"erp",         title:"ERP Enterprise Hub",           desc:"Unifies sales, ops, supply chain, and finance into one database.",      tag:"ERP • CORE HUB",       tagColor:"border-amber-500/40 text-amber-400 bg-amber-500/10",       legacy:"SAP / Oracle Cloud",    category:"finance",       icon:"layers",          costPerUser:180, integration:"Enterprise data bus connector for legacy system coexistence.",                              replacement:"Single unified core database replacing complex legacy ERP monoliths." },
  { id:"bank",        title:"Bank Reconciliation",          desc:"Matches electronic bank feeds with general ledger deposits.",           tag:"BANK • FEED SYNC",     tagColor:"border-blue-500/40 text-blue-400 bg-blue-500/10",         legacy:"Plaid / Xero",          category:"finance",       icon:"landmark",        costPerUser: 25, integration:"Plaid & Yodlee open-banking feed ingestion pipelines.",                                     replacement:"Automated rule-based ledger matching and real-time bank statement audit." },
  { id:"forecast",    title:"Forecast & Modeling",          desc:"Builds forward-looking revenue projections & headcounts.",              tag:"FP&A • PREDICTIVE",    tagColor:"border-emerald-500/40 text-emerald-400 bg-emerald-500/10", legacy:"Anaplan / Mosaic",     category:"finance",       icon:"line-chart",      costPerUser: 85, integration:"Financial data dump export for specialized external modeling.",                            replacement:"Native Monte Carlo simulation & predictive revenue growth forecasting." },
  { id:"intake",      title:"Dynamic Intake Forms",         desc:"Captures customer data through interactive conditional surveys.",        tag:"INTAKE • READY",       tagColor:"border-cyan-500/40 text-cyan-400 bg-cyan-500/10",         legacy:"Typeform / Jotform",    category:"productivity",  icon:"check-square",    costPerUser: 15, integration:"Form submission webhooks and CRM payload auto-mapping.",                                    replacement:"Built-in dynamic logic form builder directly writing to database records." },
  { id:"subscription",title:"Subscription Billing",         desc:"Handles tier upgrades, prorations, and dunning management.",             tag:"RECURRING • AUTO",     tagColor:"border-orange-500/40 text-orange-400 bg-orange-500/10",   legacy:"Chargebee / Recurly",   category:"finance",       icon:"refresh-cw",      costPerUser: 50, integration:"Syncs subscription statuses and customer ledger balances.",                                 replacement:"Automated proration engine, custom SaaS pricing tiers, and automated dunning." },
  { id:"team",        title:"Team Communication",           desc:"Facilitates real-time channel chat, huddles, and file sharing.",         tag:"CHAT • ONLINE",        tagColor:"border-purple-500/40 text-purple-400 bg-purple-500/10",   legacy:"Slack / MS Teams",      category:"productivity",  icon:"message-square",  costPerUser: 18, integration:"Bot notifications and slash-command integrations.",                                          replacement:"Embedded contextual chat threads directly inside deals, tasks, and documents." },
  { id:"audit",       title:"Audit & Security Logs",        desc:"Maintains automated evidence logs for SOC2 & ISO compliance.",           tag:"SOC2 • VERIFIED",      tagColor:"border-rose-500/40 text-rose-400 bg-rose-500/10",         legacy:"Vanta / Drata",         category:"hr",            icon:"shield-alert",    costPerUser: 40, integration:"Compliance telemetry event stream streaming.",                                              replacement:"Automated continuous compliance evidence collector and immutability log." },
];

/* ── lucide-react name resolution — matches the data-lucide strings above ── */
const ICONS = {
  cpu:Cpu, workflow:Workflow, layers:Layers, calculator:Calculator, download:Download,
  "badge-dollar-sign":BadgeDollarSign, "box-select":BoxSelect, zap:Zap, "shield-check":ShieldCheck,
  search:Search, "mouse-pointer-click":MousePointerClick, users:Users, "trending-up":TrendingUp,
  package:Package, "file-text":FileText, "credit-card":CreditCard, "user-check":UserCheck,
  "bar-chart-3":BarChart3, kanban:Kanban, headphones:Headphones, "file-signature":FileSignature,
  shield:Shield, megaphone:Megaphone, landmark:Landmark, "line-chart":LineChart,
  "check-square":CheckSquare, "refresh-cw":RefreshCw, "message-square":MessageSquare,
  "shield-alert":ShieldAlert, "arrow-up-right":ArrowUpRight, x:X,
  "check-circle-2":CheckCircle2, "trending-down":TrendingDown, box:Box, home:Home,
};
const Ico = ({ name, className, ...rest }) => {
  const I = ICONS[name] || Package;
  return <I className={className} {...rest} aria-hidden="true" />;
};

const FILTERS = [
  { key:"all",          label:`All Modules (${CATEGORIES.length})` },
  { key:"sales",        label:"Sales & Marketing" },
  { key:"finance",      label:"Finance & Ops" },
  { key:"hr",           label:"HR & Security" },
  { key:"productivity", label:"Productivity" },
];

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

/**
 * /demo — verbatim port of the "Nexengines Unified Enterprise Platform"
 * dashboard HTML export. 1:1 palette, copy, and interactivity:
 *  • Mode switcher (Hybrid / Unified) — Unified shows per-card annual savings
 *  • Category filters + free-text search
 *  • 20 module tiles with a detail modal + ROI calculator (slider 10-1000 seats)
 *  • Export Audit downloads a .txt report
 *  • "Launch Live Demo" inside the modal is sandboxed (toast, no real writes)
 *
 * Kept out of the marketing PublicShell chrome via App.js `bareRoute` so the
 * dashboard fills the full page width.
 */
export default function DemoPage() {
  const [mode, setMode]           = useState("unified");     // 'hybrid' | 'unified'
  const [filter, setFilter]       = useState("all");
  const [search, setSearch]       = useState("");
  const [seats, setSeats]         = useState(100);
  const [selected, setSelected]   = useState(null);          // module for detail modal
  const [calcOpen, setCalcOpen]   = useState(false);

  const totalPerUserMonthly = useMemo(
    () => CATEGORIES.reduce((sum, c) => sum + c.costPerUser, 0),
    []
  );
  const legacyAnnual = totalPerUserMonthly * seats * 12;
  const nexAnnual    = legacyAnnual * 0.20;                  // 80% savings estimate
  const netSavings   = legacyAnnual - nexAnnual;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return CATEGORIES.filter((c) => {
      const okFilter = filter === "all" || c.category === filter;
      if (!okFilter) return false;
      if (!q) return true;
      return c.title.toLowerCase().includes(q)
          || c.legacy.toLowerCase().includes(q)
          || c.desc.toLowerCase().includes(q);
    });
  }, [filter, search]);

  const exportReport = () => {
    const text =
      `NEXENGINES CONSOLIDATION & AUDIT REPORT\n` +
      `==========================================\n` +
      `Target User Seats: ${seats}\n` +
      `Categories Consolidated: ${CATEGORIES.length}\n` +
      `Estimated Annual Savings: ${fmt(netSavings)} / yr\n\n` +
      CATEGORIES.map((c) => `- ${c.title} (${c.legacy}): Saves ${fmt(c.costPerUser * seats * 12)}/yr`).join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "Nexengines_Consolidation_Report.txt";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const launchDemo = () => {
    if (!selected) return;
    toast.success(`Demo · "${selected.title}" deployment preview requested — book a real audit to go live.`);
    setSelected(null);
  };

  return (
    <div className="nex-demo-scope bg-navy-950 text-slate-100 font-sans min-h-screen antialiased selection:bg-brand-blue selection:text-white pb-16" data-testid="nex-demo-v2">
      {/* Scoped styles — glass-morphism, custom scrollbar, gradient text. */}
      <style>{`
        .nex-demo-scope { font-family: Inter, ui-sans-serif, system-ui, sans-serif; }
        .nex-demo-scope .font-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .nex-demo-scope ::-webkit-scrollbar { width: 6px; height: 6px; }
        .nex-demo-scope ::-webkit-scrollbar-track { background: #0c1322; }
        .nex-demo-scope ::-webkit-scrollbar-thumb { background: #233352; border-radius: 3px; }
        .nex-demo-scope ::-webkit-scrollbar-thumb:hover { background: #3b82f6; }
        .nex-demo-scope .glass-card {
          background: rgba(17, 26, 46, 0.75);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
        }
        .nex-demo-scope .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.4);
          box-shadow: 0 12px 40px 0 rgba(6, 182, 212, 0.15);
        }
        .nex-demo-scope .glass-modal {
          background: rgba(12, 19, 34, 0.95);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.12);
        }
        .nex-demo-scope .gradient-emerald {
          background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      `}</style>

      {/* ── Header ── */}
      <header className="sticky top-0 z-40 bg-navy-900/90 backdrop-blur-md border-b border-slate-800/80 px-4 lg:px-8 py-3.5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="demo-brand-lockup"
            title="Back to Nexengines home"
            className="flex items-center gap-3 shrink-0"
          >
            <img
              src={LIFE_LOGO}
              alt="Learning Institute For Empowerment"
              className="h-10 w-10 object-contain"
            />
            <div className="hidden sm:flex items-center gap-3">
              <div className="w-px h-8 bg-nex-border" />
              <NexWordmark size="text-xl" />
            </div>
            <span className="hidden md:inline-block text-[10px] font-mono font-semibold px-2 py-0.5 rounded bg-brand-blue/10 text-brand-cyan border border-brand-blue/30 tracking-[0.14em] uppercase">
              v4.8 · live demo
            </span>
          </Link>

          <div className="flex items-center bg-navy-950 p-1 rounded-xl border border-slate-800">
            <button
              data-testid="mode-hybrid-btn"
              onClick={() => setMode("hybrid")}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                mode === "hybrid"
                  ? "bg-gradient-to-r from-brand-blue to-brand-cyan text-white shadow-md"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              <Ico name="workflow" className={`w-4 h-4 ${mode === "hybrid" ? "" : "text-brand-cyan"}`} />
              <span>Hybrid Mode (API Sync)</span>
            </button>
            <button
              data-testid="mode-unified-btn"
              onClick={() => setMode("unified")}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                mode === "unified"
                  ? "bg-gradient-to-r from-brand-blue to-brand-cyan text-white shadow-md"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              <Ico name="layers" className="w-4 h-4" />
              <span>Unified Mode (Full Replacement)</span>
            </button>
          </div>

          <div className="hidden lg:flex items-center space-x-3">
            <Link
              to="/"
              data-testid="demo-home-btn"
              title="Back to Nexengines home"
              className="flex items-center space-x-2 px-3.5 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-200 hover:text-white border border-white/10 hover:border-white/30 text-xs font-medium transition"
            >
              <Ico name="home" className="w-4 h-4" />
              <span>Home</span>
            </Link>
            <button
              data-testid="demo-open-calc"
              onClick={() => setCalcOpen(true)}
              className="flex items-center space-x-2 px-3.5 py-2 rounded-lg bg-navy-850 hover:bg-navy-800 text-slate-300 border border-slate-700/60 text-xs font-medium transition"
            >
              <Ico name="calculator" className="w-4 h-4 text-brand-amber" />
              <span>ROI Calculator</span>
            </button>
            <button
              data-testid="demo-export"
              onClick={exportReport}
              className="flex items-center space-x-2 px-3.5 py-2 rounded-lg bg-brand-blue/20 hover:bg-brand-blue/30 text-brand-cyan border border-brand-blue/40 text-xs font-medium transition"
            >
              <Ico name="download" className="w-4 h-4" />
              <span>Export Audit</span>
            </button>
          </div>
        </div>
      </header>

      {/* ── Metric Savings Bar ── */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pt-6">
        <div className="glass-card rounded-2xl p-5 border border-slate-800 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-80 h-80 bg-brand-blue/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-brand-emerald/10 rounded-full blur-3xl pointer-events-none" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            <Kpi icon="badge-dollar-sign" tint="emerald" label="Est. Total Savings" sub="Based on 100 User Enterprise License" divider>
              <span data-testid="kpi-total-savings" className="gradient-emerald">{fmt(netSavings)} / yr</span>
            </Kpi>
            <Kpi icon="box-select" tint="cyan" label="Redundant SaaS Apps" sub="Consolidated into 1 Control Center" divider>
              <span className="text-slate-100">{CATEGORIES.length} Categories</span>
            </Kpi>
            <Kpi icon="zap" tint="purple" label="Data Sync Overhead" sub="Zero API rate limits or Zap failures" divider>
              <span className="text-slate-100">-94% Latency</span>
            </Kpi>
            <Kpi icon="shield-check" tint="blue" label="Security Attack Surface" sub="SOC2 Type II + Automated Auditing">
              <span className="text-slate-100">Single Perimeter</span>
            </Kpi>
          </div>
        </div>
      </section>

      {/* ── LIVE OPERATOR TELEMETRY — real /api/nex-os data, sandboxed writes ── *
       * KPI pulse + live feed + platform-ops KPIs + 10-step revenue flow +
       * 24-capability roadmap + 20-module SaaS consolidation grid.  All
       * four tiers are wrapped in `<AccordionSection>` so the demo mirrors
       * the real `/os` operator surface. */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 mt-6" data-testid="demo-live-embeds">
        <NdsLiveEmbeds
          filterBar={
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative w-full md:w-80">
                <Ico name="search" className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="demo-search"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search CRM, Billing, Zapier, etc..."
                  className="w-full bg-navy-950 border border-slate-700/80 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-brand-blue transition"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    data-testid={`demo-filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={
                      filter === f.key
                        ? "text-xs px-3 py-1.5 rounded-lg border border-brand-blue bg-brand-blue/20 text-brand-cyan font-medium transition"
                        : "text-xs px-3 py-1.5 rounded-lg border border-slate-800 bg-navy-950 text-slate-400 hover:text-slate-200 transition"
                    }
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="hidden xl:flex items-center space-x-1 text-xs text-brand-cyan font-mono bg-brand-blue/10 px-3 py-1.5 rounded-lg border border-brand-blue/20">
                <Ico name="mouse-pointer-click" className="w-3.5 h-3.5 animate-bounce" />
                <span>Click any module for deep audit</span>
              </div>
            </div>
          }
          grid={
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="demo-grid">
              {visible.map((item) => {
                const monthlyLegacy = item.costPerUser * seats;
                const yearlySavings = monthlyLegacy * 12;
                return (
                  <button
                    type="button"
                    key={item.id}
                    data-testid={`demo-card-${item.id}`}
                    onClick={() => setSelected(item)}
                    className="text-left glass-card rounded-xl p-4 flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 cursor-pointer group relative overflow-hidden"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="p-1.5 rounded-lg bg-navy-950 border border-slate-800 text-slate-300 group-hover:border-brand-blue/50 group-hover:text-brand-cyan transition">
                          <Ico name={item.icon} className="w-4 h-4" />
                        </div>
                        <span className={`text-[9px] font-mono font-bold px-2 py-0.5 rounded ${item.tagColor} border uppercase tracking-wider`}>
                          {item.tag}
                        </span>
                      </div>
                      <h3 className="font-bold text-slate-100 text-sm group-hover:text-brand-cyan transition line-clamp-1">{item.title}</h3>
                      <p className="text-slate-400 text-xs mt-1.5 leading-relaxed line-clamp-2">{item.desc}</p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] font-mono">
                      <span className="text-slate-500 truncate max-w-[120px]">{item.legacy}</span>
                      <span className="text-brand-cyan flex items-center group-hover:translate-x-0.5 transition-transform">
                        {mode === "unified" ? (
                          <span className="text-emerald-400 font-bold">-{fmt(yearlySavings)}/yr</span>
                        ) : (
                          <>OPEN <Ico name="arrow-up-right" className="w-3 h-3 ml-0.5 inline" /></>
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
              {visible.length === 0 && (
                <div className="col-span-full text-center py-16 text-slate-400 text-sm font-mono" data-testid="demo-empty">
                  No modules match your search.
                </div>
              )}
            </div>
          }
        />
      </section>

      {/* ── Detail Modal ── */}
      {selected && <DetailModal item={selected} seats={seats} onClose={() => setSelected(null)} onLaunch={launchDemo} />}

      {/* ── ROI Calculator Modal ── */}
      {calcOpen && (
        <CalcModal
          seats={seats} setSeats={setSeats}
          legacyAnnual={legacyAnnual} nexAnnual={nexAnnual} netSavings={netSavings}
          onClose={() => setCalcOpen(false)}
        />
      )}
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────── */

const TINT = {
  emerald: { bg:"bg-brand-emerald/10", border:"border-brand-emerald/20", text:"text-brand-emerald" },
  cyan:    { bg:"bg-brand-cyan/10",    border:"border-brand-cyan/20",    text:"text-brand-cyan"    },
  purple:  { bg:"bg-brand-purple/10",  border:"border-brand-purple/20",  text:"text-brand-purple"  },
  blue:    { bg:"bg-brand-blue/10",    border:"border-brand-blue/20",    text:"text-brand-blue"    },
};

function Kpi({ icon, tint, label, sub, children, divider }) {
  const t = TINT[tint] || TINT.blue;
  return (
    <div className={`flex items-center space-x-4 pr-4 pb-4 md:pb-0 ${divider ? "border-b md:border-b-0 md:border-r border-slate-800" : ""}`}>
      <div className={`p-3 rounded-xl border ${t.bg} ${t.border} ${t.text}`}>
        <Ico name={icon} className="w-7 h-7" />
      </div>
      <div>
        <div className="text-xs uppercase font-mono tracking-wider text-slate-400">{label}</div>
        <div className="text-2xl font-black font-mono tracking-tight">{children}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>
      </div>
    </div>
  );
}

function DetailModal({ item, seats, onClose, onLaunch }) {
  const monthlyLegacy = item.costPerUser * seats;
  const annualSavings = monthlyLegacy * 12;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/80 backdrop-blur-md transition-all duration-300"
      onClick={onClose}
      data-testid="demo-detail-modal"
    >
      <div
        className="glass-modal w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl border border-slate-700/80 transform transition-all duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between bg-navy-900/80">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-brand-blue/20 text-brand-cyan border border-brand-blue/30">
              <Ico name={item.icon} className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold text-white">{item.title}</h3>
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded font-bold ${item.tagColor} border`}>{item.tag}</span>
              </div>
              <p className="text-xs text-slate-400 font-mono mt-0.5">3rd Party Tools: {item.legacy}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="demo-detail-close" className="w-8 h-8 rounded-lg bg-navy-850 border border-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition">
            <Ico name="x" className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          <p className="text-slate-300 text-sm leading-relaxed">{item.desc}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-navy-900/60 rounded-xl p-4 border border-brand-blue/20 hover:border-brand-blue/50 transition">
              <div className="flex items-center space-x-2 text-brand-cyan mb-3">
                <Ico name="workflow" className="w-4 h-4" />
                <h4 className="font-bold text-xs uppercase tracking-wider font-mono">1. Bi-Directional Integration</h4>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed mb-3">{item.integration}</p>
              <div className="bg-navy-950 p-2.5 rounded-lg border border-slate-800/80 font-mono text-[11px] text-slate-400 flex items-center justify-between">
                <span>Sync Latency:</span>
                <span className="text-emerald-400 font-semibold">&lt; 15ms Real-Time</span>
              </div>
            </div>

            <div className="bg-navy-900/60 rounded-xl p-4 border border-brand-emerald/20 hover:border-brand-emerald/50 transition">
              <div className="flex items-center space-x-2 text-brand-emerald mb-3">
                <Ico name="box" className="w-4 h-4" />
                <h4 className="font-bold text-xs uppercase tracking-wider font-mono">2. Native Nexengine Replacement</h4>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed mb-3">{item.replacement}</p>
              <div className="bg-navy-950 p-2.5 rounded-lg border border-slate-800/80 font-mono text-[11px] text-slate-400 flex items-center justify-between">
                <span>Feature Parity:</span>
                <span className="text-brand-cyan font-semibold">100% Native Coverage</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-navy-900 via-navy-850 to-navy-900 p-4 rounded-xl border border-brand-amber/30">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Ico name="trending-down" className="w-4 h-4 text-brand-amber" />
                <span className="text-xs font-bold font-mono text-slate-200 uppercase tracking-wider">Consolidation Economics</span>
              </div>
              <span className="text-[11px] font-mono text-slate-400">Calculated for {seats} Seats</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-center pt-2 border-t border-slate-800">
              <div className="p-2 bg-navy-950/60 rounded-lg">
                <div className="text-[10px] text-slate-400 font-mono">3rd Party Cost</div>
                <div className="text-sm font-bold font-mono text-rose-400">{fmt(monthlyLegacy)} / mo</div>
              </div>
              <div className="p-2 bg-navy-950/60 rounded-lg">
                <div className="text-[10px] text-slate-400 font-mono">Nexengines Native</div>
                <div className="text-sm font-bold font-mono text-emerald-400">Included</div>
              </div>
              <div className="p-2 bg-navy-950/60 rounded-lg col-span-2 md:col-span-1">
                <div className="text-[10px] text-slate-400 font-mono">Annual Net Savings</div>
                <div className="text-sm font-black font-mono text-brand-cyan">{fmt(annualSavings)} / yr</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-navy-950 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center space-x-2 text-xs text-slate-400">
            <Ico name="check-circle-2" className="w-4 h-4 text-emerald-400" />
            <span>Ready to deploy in 1-Click Sandbox</span>
          </div>
          <div className="flex items-center space-x-2 w-full sm:w-auto">
            <button onClick={onClose} className="w-full sm:w-auto px-4 py-2 rounded-lg bg-navy-850 hover:bg-navy-800 text-slate-300 border border-slate-700 text-xs font-medium transition">
              Close
            </button>
            <button onClick={onLaunch} data-testid="demo-launch" className="w-full sm:w-auto px-4 py-2 rounded-lg bg-gradient-to-r from-brand-blue to-brand-cyan text-white text-xs font-semibold shadow-lg shadow-cyan-500/20 transition">
              Launch Live Demo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CalcModal({ seats, setSeats, legacyAnnual, nexAnnual, netSavings, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/80 backdrop-blur-md transition-all duration-300" onClick={onClose} data-testid="demo-calc-modal">
      <div className="glass-modal w-full max-w-xl rounded-2xl p-6 shadow-2xl border border-slate-700 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <Ico name="x" className="w-5 h-5" />
        </button>
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-3 bg-brand-amber/10 rounded-xl border border-brand-amber/20 text-brand-amber">
            <Ico name="calculator" className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Custom Savings Calculator</h3>
            <p className="text-xs text-slate-400">Adjust team headcount to model software license consolidation ROI</p>
          </div>
        </div>

        <div className="space-y-5 my-6">
          <div>
            <div className="flex justify-between text-xs font-mono mb-2">
              <span className="text-slate-300">Active Team Seats / Users:</span>
              <span className="text-brand-cyan font-bold text-sm">{seats} Seats</span>
            </div>
            <input
              data-testid="demo-seat-slider"
              type="range" min={10} max={1000} value={seats} step={10}
              onChange={(e) => setSeats(parseInt(e.target.value, 10))}
              className="w-full h-2 bg-navy-900 rounded-lg appearance-none cursor-pointer accent-brand-cyan"
            />
            <div className="flex justify-between text-[10px] text-slate-500 font-mono mt-1">
              <span>10 Users</span><span>500 Users</span><span>1,000 Users</span>
            </div>
          </div>
          <div className="p-4 bg-navy-900/80 rounded-xl border border-slate-800 space-y-3 font-mono text-xs">
            <div className="flex justify-between">
              <span className="text-slate-400">Estimated SaaS Stack Cost:</span>
              <span className="text-rose-400 font-bold">{fmt(legacyAnnual)} / yr</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Nexengines Unified Subscription:</span>
              <span className="text-emerald-400 font-bold">{fmt(nexAnnual)} / yr</span>
            </div>
            <div className="border-t border-slate-800 pt-2 flex justify-between text-sm">
              <span className="text-white font-bold">Estimated Net Annual ROI:</span>
              <span className="text-brand-cyan font-black text-base">{fmt(netSavings)} / yr</span>
            </div>
          </div>
        </div>

        <button onClick={onClose} className="w-full py-2.5 rounded-lg bg-gradient-to-r from-brand-blue to-brand-cyan text-white text-xs font-semibold shadow-lg shadow-cyan-500/20">
          Apply Calculation Model
        </button>
      </div>
    </div>
  );
}


/** Live operator telemetry stack — mirrors the top two tiers of /os.
 *  Wraps the NDS components in the same provider stack the operator
 *  console uses, with `demoMode` on so mutations are short-circuited. */
function NdsLiveEmbeds({ filterBar, grid }) {
  return (
    <DemoModeProvider value>
      <NexOsProvider demoMock>
        <FloatingWindowsProvider>
          <NdsEditorProvider>
            <div className="nds-scope flex flex-col gap-6" data-testid="demo-nds-scope">
              {/* Tier 1 — KPI pulse + live feed side by side, then platform-ops KPIs */}
              <AccordionSection
                id="kpi_demo"
                kicker="// TIER 1"
                title="Live KPIs · Real-time pulse + Platform ops"
              >
                <div className="flex flex-col gap-6">
                  <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
                    <KpiPulseBar />
                    <LiveSystemFeed />
                  </div>
                  <OpsKpiBar />
                </div>
              </AccordionSection>

              {/* Tier 2 — the 10-Step Revenue Flow */}
              <AccordionSection
                id="flow_demo"
                kicker="// TIER 2"
                title="10-Step Revenue Flow · Universal lifecycle"
              >
                <TenStepRevenueFlow />
              </AccordionSection>

              {/* Tier 3 — full 24-capability roadmap with search, category
                  filters, tier chips, pin-to-Custom stars, and inline
                  rename via the pencil icon. Renames don't persist in
                  demoMode (saveItemPatch short-circuits the PUT) but the
                  UX is identical to /os so prospects feel the full
                  operator toolkit. */}
              <AccordionSection
                id="caps_demo"
                kicker="// TIER 3"
                title="Modular Capabilities · 24-card roadmap"
              >
                <CapabilityRoadmap />
              </AccordionSection>

              {/* Tier 4 — 20-module SaaS consolidation grid.  Each tile
                  swaps for a native Nexengines capability — this is the
                  core "replace your stack" pitch for prospects. */}
              {(filterBar || grid) && (
                <AccordionSection
                  id="saas_demo"
                  kicker="// TIER 4"
                  title="SaaS Consolidation Map · 20 modules replaced"
                >
                  <div className="flex flex-col gap-4">
                    {filterBar}
                    {grid}
                  </div>
                </AccordionSection>
              )}

              {/* Floating-window layer — non-blocking, only fires if the
                  operator clicks into a step tile. Actions are sandboxed. */}
              <WindowLayer />
            </div>
          </NdsEditorProvider>
        </FloatingWindowsProvider>
      </NexOsProvider>
    </DemoModeProvider>
  );
}
